import StepsHeroSection from "../components/landing/StepsHeroSection"
import LandingTextSection from "../components/landing/LandingTextSection"
import FounderSection from "../components/landing/FounderSection"
import AboutExpertiseSection from "../components/landing/AboutExpertiseSection"
import CurriculumVitaeSection from "../components/landing/CurriculumVitaeSection"
import Footer from "../components/ui/Footer"
import { useSiteContent } from "../hooks/useSiteContent"

const DEFAULT_FOUNDER_PARAGRAPHS = [
  "Dr. Dirk Dantz is a European Patent Attorney with extensive experience in intellectual property, technology and industry.",
  "His professional background includes many years in C-level and senior IP positions within industrial companies. This experience enables him to combine legal and technical IP expertise with a clear understanding of business models, competitive environments and commercial objectives.",
  "His work focuses on strategic IP advice and the development and implementation of IP strategies tailored to the individual objectives of each client.",
  "Dr. Dantz also has extensive experience in the valuation and licensing of IP portfolios, with a track record in the nine-figure EUR range, as well as in negotiating and drafting cooperation and technology-related agreements.",
]

const DEFAULT_CREDENTIAL_LINES = [
  "Dr. rer. nat. Dipl.-Min.",
  "European Patent Attorney",
  "info@xpress-ip.com",
]

const DEFAULT_TECHNICAL_FIELDS = [
  "Physics",
  "Organic chemistry",
  "Mechanical engineering",
  "Semiconductor technology",
  "Medical technology",
  "Laser technology",
  "Automotive technology",
  "Energy technology",
  "Materials technology",
  "Information and communication technology",
  "Software",
  "Consumer goods",
]

const DEFAULT_AREAS_OF_ACTIVITY = [
  {
    title: "Strategic IP management",
    text: "Strategic management consulting and development of IP strategies aligned with business objectives.",
  },
  {
    title: "IP portfolio development",
    text: "Strategic development and management of patent, trademark and design portfolios.",
  },
  {
    title: "IP generation",
    text: "Drafting and filing patents, utility models, trademarks and designs.",
  },
  {
    title: "Prosecution",
    text: "Conducting proceedings for the grant of industrial property rights.",
  },
  {
    title: "Opposition & appeal",
    text: "Defending IP rights in opposition, nullity and appeal proceedings.",
  },
  {
    title: "Enforcement",
    text: "Enforcement of IP rights in and out of court, including patent and trademark infringement matters.",
  },
  {
    title: "Technology transfer",
    text: "Transfer of intellectual property rights and technologies.",
  },
  {
    title: "Licensing",
    text: "Licensing law and licensing agreements.",
  },
  {
    title: "IP valuation",
    text: "Valuation of intellectual property portfolios.",
  },
  {
    title: "Freedom to operate",
    text: "Freedom-to-operate analyses.",
  },
  {
    title: "Due diligence",
    text: "Preparation and execution of IP due diligence.",
  },
]

const DEFAULT_EXPERIENCES = [
  {
    period: "Since 10/2015",
    lines: ["ip assets licensing GmbH, Zug, Switzerland", "CEO, Owner"],
  },
  {
    period: "Since 07/2013",
    lines: ["DANTZ IP & LAW", "European Patent Attorney, Owner"],
  },
  {
    period: "10/2010 to 09/2015",
    lines: ["ipal GmbH, Berlin", "CEO"],
  },
  {
    period: "05/2009 to 09/2010",
    lines: ["REV Renewable Energy Ventures Inc., Zug, Switzerland", "CEO"],
  },
  {
    period: "04/2008 to 04/2009",
    lines: [
      "City Solar AG, Bad Kreuznach",
      "Vice President, Intellectual Property Management",
    ],
  },
  {
    period: "01/2006 to 03/2008",
    lines: [
      "Faurecia Autositze GmbH, Stadthagen",
      "Head of Department, Intellectual Property Germany",
    ],
  },
  {
    period: "03/2004 to 12/2005",
    lines: ["Volkswagen AG, Wolfsburg", "IP Manager, Electronic"],
  },
  {
    period: "01/2001 to 03/2004",
    lines: ["Wacker Siltronic AG, Burghausen", "Technology / R&D Management"],
  },
  {
    period: "03/1997 to 12/2000",
    lines: ["Hahn-Meitner-Institut, Berlin", "PhD Student, Postdoc"],
  },
]

export default function AboutPage() {
  const site = useSiteContent("about-us")

  const technicalFields = DEFAULT_TECHNICAL_FIELDS.map((field, index) =>
    site.get(`about-us.expertise.field.${index}`, field)
  )

  const areasOfActivity = DEFAULT_AREAS_OF_ACTIVITY.map((area, index) => ({
    title: site.get(`about-us.expertise.area.${index}.title`, area.title),
    text: site.get(`about-us.expertise.area.${index}.text`, area.text),
  }))

  const experiences = DEFAULT_EXPERIENCES.map((experience, index) => ({
    period: site.get(`about-us.cv.experience.${index}.period`, experience.period),
    lines: experience.lines.map((line, lineIndex) =>
      site.get(`about-us.cv.experience.${index}.line.${lineIndex}`, line)
    ),
  }))

  return (
    <div className="flex min-h-screen flex-col">
      {site.isAdmin && (
        <button
          type="button"
          onClick={() => site.setEditMode(!site.editMode)}
          className={`fixed bottom-6 right-6 z-50 rounded-full border-2 border-white px-5 py-3 text-sm font-semibold shadow-xl transition ${
            site.editMode ? "bg-orange-500 text-white" : "bg-dantzBlue text-white"
          }`}
        >
          {site.editMode ? "Done editing" : "Edit page"}
        </button>
      )}

      <StepsHeroSection
        backgroundImage="/images/design.svg"
        heading={site.get("about-us.hero.heading", "ABOUT US")}
        description={site.get(
          "about-us.hero.description",
          "European IP expertise. Digital access. xpress-ip combines an efficient online filing platform with professional European IP representation by DANTZ IP & LAW."
        )}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("about-us.hero.heading", value)}
        onSaveDescription={(value) => site.save("about-us.hero.description", value)}
      />

      <LandingTextSection
        heading={site.get(
          "about-us.intro.heading",
          "PROFESSIONAL EUROPEAN IP REPRESENTATION"
        )}
        subheading={site.get(
          "about-us.intro.subheading",
          "With a simple digital process"
        )}
        divider={false}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("about-us.intro.heading", value)}
        onSaveSubheading={(value) => site.save("about-us.intro.subheading", value)}
        body={[
          site.get(
            "about-us.intro.body.0",
            "Our goal is simple: make European IP filing easier for companies, inventors and IP professionals around the world."
          ),
          site.get(
            "about-us.intro.body.1",
            "Instead of navigating different European filing procedures and administrative requirements yourself, you can provide the required information and documents online and let our European Patent Attorneys handle the filing process."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`about-us.intro.body.${index}`, value)}
      />

      <FounderSection
        name={site.get("about-us.founder.name", "DR. DIRK DANTZ")}
        role={site.get("about-us.founder.role", "Founder and owner")}
        paragraphs={DEFAULT_FOUNDER_PARAGRAPHS.map((paragraph, index) =>
          site.get(`about-us.founder.body.${index}`, paragraph)
        )}
        photoName={site.get("about-us.founder.photoName", "DR. DIRK DANTZ")}
        credentialLines={DEFAULT_CREDENTIAL_LINES.map((line, index) =>
          site.get(`about-us.founder.credential.${index}`, line)
        )}
        editable={site.editMode}
        onSaveName={(value) => site.save("about-us.founder.name", value)}
        onSaveRole={(value) => site.save("about-us.founder.role", value)}
        onSaveParagraph={(index, value) =>
          site.save(`about-us.founder.body.${index}`, value)
        }
        onSavePhotoName={(value) => site.save("about-us.founder.photoName", value)}
        onSaveCredentialLine={(index, value) =>
          site.save(`about-us.founder.credential.${index}`, value)
        }
      />

      <AboutExpertiseSection
        heading1={site.get("about-us.expertise.heading1", "TECHNICAL EXPERIENCE")}
        intro={site.get(
          "about-us.expertise.intro",
          "Dr. Dantz has worked successfully in a broad range of technical fields, including:"
        )}
        technicalFields={technicalFields}
        heading2={site.get("about-us.expertise.heading2", "MAIN AREAS OF ACTIVITY")}
        areasOfActivity={areasOfActivity}
        editable={site.editMode}
        onSaveHeading1={(value) => site.save("about-us.expertise.heading1", value)}
        onSaveIntro={(value) => site.save("about-us.expertise.intro", value)}
        onSaveField={(index, value) =>
          site.save(`about-us.expertise.field.${index}`, value)
        }
        onSaveHeading2={(value) => site.save("about-us.expertise.heading2", value)}
        onSaveAreaTitle={(index, value) =>
          site.save(`about-us.expertise.area.${index}.title`, value)
        }
        onSaveAreaText={(index, value) =>
          site.save(`about-us.expertise.area.${index}.text`, value)
        }
      />

      <CurriculumVitaeSection
        name={site.get("about-us.cv.name", "DR. DIRK DANTZ")}
        credentials={site.get(
          "about-us.cv.credentials",
          "Dr. rer. nat. · Dipl.-Min. · European Patent Attorney"
        )}
        subheading={site.get("about-us.cv.subheading", "Curriculum vitae")}
        experienceLabel={site.get(
          "about-us.cv.experienceLabel",
          "Professional experience"
        )}
        experiences={experiences}
        rolesLabel={site.get("about-us.cv.rolesLabel", "Additional roles")}
        rolesText={site.get(
          "about-us.cv.rolesText",
          "Dr. Dantz is also an expert for the IBB ProFIT funding programme on behalf of the State of Berlin and serves as an advisory board member for the Berlin Brandenburg Innovation Award."
        )}
        editable={site.editMode}
        onSaveName={(value) => site.save("about-us.cv.name", value)}
        onSaveCredentials={(value) => site.save("about-us.cv.credentials", value)}
        onSaveSubheading={(value) => site.save("about-us.cv.subheading", value)}
        onSaveExperienceLabel={(value) =>
          site.save("about-us.cv.experienceLabel", value)
        }
        onSaveExperiencePeriod={(index, value) =>
          site.save(`about-us.cv.experience.${index}.period`, value)
        }
        onSaveExperienceLine={(index, lineIndex, value) =>
          site.save(`about-us.cv.experience.${index}.line.${lineIndex}`, value)
        }
        onSaveRolesLabel={(value) => site.save("about-us.cv.rolesLabel", value)}
        onSaveRolesText={(value) => site.save("about-us.cv.rolesText", value)}
      />

      <LandingTextSection
        heading={site.get("about-us.why.heading", "WHY THIS MATTERS FOR XPRESS-IP")}
        subheading={site.get(
          "about-us.why.subheading",
          "Digital filing. Real European IP expertise."
        )}
        variant="dark"
        editable={site.editMode}
        onSaveHeading={(value) => site.save("about-us.why.heading", value)}
        onSaveSubheading={(value) => site.save("about-us.why.subheading", value)}
        body={[
          site.get(
            "about-us.why.body.0",
            "xpress-ip is backed by an established European patent law firm."
          ),
          site.get(
            "about-us.why.body.1",
            "This means that the convenience of an online platform does not come at the expense of professional European representation."
          ),
          site.get(
            "about-us.why.body.2",
            "You get the simplicity of digital filing, and the expertise of European Patent Attorneys."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`about-us.why.body.${index}`, value)}
      />

      <Footer
        editable={site.editMode}
        email={site.get("about-us.footer.email", "info@xpress-ip.com")}
        tagline={site.get("about-us.footer.tagline", "EUROPEAN IP FILING, MADE SIMPLE.")}
        taglineSub={site.get("about-us.footer.taglineSub", "Patents · Trademarks · Designs")}
        onSaveEmail={(value) => site.save("about-us.footer.email", value)}
        onSaveTagline={(value) => site.save("about-us.footer.tagline", value)}
        onSaveTaglineSub={(value) => site.save("about-us.footer.taglineSub", value)}
      />
    </div>
  )
}
